import { useState } from 'react';
import {
  Stack, Paper, Group, Text, Button, Switch, Select, NumberInput,
  Table, Modal, Skeleton, ActionIcon, Badge,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconClock, IconPlus, IconTrash, IconEdit } from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import StatusBadge from '@/components/StatusBadge';
import {
  usePaymentSchedules, useCreatePaymentSchedule,
  useUpdatePaymentSchedule, useDeletePaymentSchedule,
} from '@/hooks/usePortalApi';
import type { PaymentSchedule } from '@/types';
import dayjs from 'dayjs';

interface ScheduleFormData {
  frequency: string;
  payment_method: string;
  max_amount: number;
  is_active: boolean;
}

export default function AutoPay() {
  const { data: schedules, isLoading } = usePaymentSchedules();
  const createSchedule = useCreatePaymentSchedule();
  const updateSchedule = useUpdatePaymentSchedule();
  const deleteSchedule = useDeletePaymentSchedule();

  const [opened, { open, close }] = useDisclosure(false);
  const [editingSchedule, setEditingSchedule] = useState<PaymentSchedule | null>(null);
  const [deleteModalOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<PaymentSchedule | null>(null);

  const [formData, setFormData] = useState<ScheduleFormData>({
    frequency: 'monthly',
    payment_method: '',
    max_amount: 0,
    is_active: true,
  });

  const frequencyOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Bi-weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'on_due_date', label: 'On Due Date' },
  ];

  const paymentMethodOptions = [
    { value: 'pm_1', label: 'Visa ending in 4242' },
    { value: 'pm_2', label: 'Mastercard ending in 5555' },
    { value: 'pm_3', label: 'ACH - Bank Account ***1234' },
  ];

  const handleOpenCreate = () => {
    setEditingSchedule(null);
    setFormData({
      frequency: 'monthly',
      payment_method: '',
      max_amount: 0,
      is_active: true,
    });
    open();
  };

  const handleOpenEdit = (schedule: PaymentSchedule) => {
    setEditingSchedule(schedule);
    setFormData({
      frequency: schedule.frequency,
      payment_method: schedule.payment_method,
      max_amount: schedule.max_amount ? parseFloat(schedule.max_amount) : 0,
      is_active: schedule.is_active,
    });
    open();
  };

  const handleSubmit = async () => {
    try {
      if (editingSchedule) {
        await updateSchedule.mutateAsync({
          id: editingSchedule.id,
          frequency: formData.frequency,
          payment_method: formData.payment_method,
          max_amount: formData.max_amount || undefined,
          is_active: formData.is_active,
        });
        notifications.show({
          title: 'Success',
          message: 'Payment schedule updated successfully',
          color: 'green',
        });
      } else {
        await createSchedule.mutateAsync({
          frequency: formData.frequency,
          payment_method: formData.payment_method,
          max_amount: formData.max_amount || undefined,
        });
        notifications.show({
          title: 'Success',
          message: 'Payment schedule created successfully',
          color: 'green',
        });
      }
      close();
    } catch {
      notifications.show({
        title: 'Error',
        message: editingSchedule
          ? 'Failed to update payment schedule'
          : 'Failed to create payment schedule',
        color: 'red',
      });
    }
  };

  const handleToggleActive = async (schedule: PaymentSchedule) => {
    try {
      await updateSchedule.mutateAsync({
        id: schedule.id,
        is_active: !schedule.is_active,
      });
      notifications.show({
        title: 'Success',
        message: `Autopay ${!schedule.is_active ? 'enabled' : 'disabled'}`,
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to update autopay status',
        color: 'red',
      });
    }
  };

  const handleOpenDelete = (schedule: PaymentSchedule) => {
    setScheduleToDelete(schedule);
    openDelete();
  };

  const handleDelete = async () => {
    if (!scheduleToDelete) return;

    try {
      await deleteSchedule.mutateAsync(scheduleToDelete.id);
      notifications.show({
        title: 'Success',
        message: 'Payment schedule deleted successfully',
        color: 'green',
      });
      closeDelete();
      setScheduleToDelete(null);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete payment schedule',
        color: 'red',
      });
    }
  };

  const getFrequencyLabel = (frequency: string) => {
    const option = frequencyOptions.find(opt => opt.value === frequency);
    return option?.label || frequency;
  };

  const getPaymentMethodLabel = (methodId: string) => {
    const option = paymentMethodOptions.find(opt => opt.value === methodId);
    return option?.label || methodId;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Stack gap="md">
        <PageHeader
          title="Autopay & Payment Scheduling"
          description="Automate your invoice payments"
        />
        <Paper p="md" withBorder>
          <Stack gap="sm">
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
          </Stack>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Autopay & Payment Scheduling"
        description="Automate your invoice payments"
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={handleOpenCreate}>
            New Schedule
          </Button>
        }
      />

      {!schedules || schedules.length === 0 ? (
        <EmptyState
          icon={<IconClock size={48} />}
          title="No payment schedules"
          description="Create a schedule to automate your payments"
          action={
            <Button leftSection={<IconPlus size={16} />} onClick={handleOpenCreate}>
              Create First Schedule
            </Button>
          }
        />
      ) : (
        <Paper withBorder>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Autopay</Table.Th>
                <Table.Th>Frequency</Table.Th>
                <Table.Th>Payment Method</Table.Th>
                <Table.Th>Max Amount</Table.Th>
                <Table.Th>Next Run</Table.Th>
                <Table.Th>Last Status</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {schedules.map((schedule) => (
                <Table.Tr key={schedule.id}>
                  <Table.Td>
                    <Switch
                      checked={schedule.is_active}
                      onChange={() => handleToggleActive(schedule)}
                      label={schedule.is_active ? 'On' : 'Off'}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light">
                      {getFrequencyLabel(schedule.frequency)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{getPaymentMethodLabel(schedule.payment_method)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {schedule.max_amount ? formatCurrency(parseFloat(schedule.max_amount)) : 'No limit'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {schedule.next_run_at
                        ? dayjs(schedule.next_run_at).format('MMM D, YYYY')
                        : 'Not scheduled'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {schedule.last_status ? (
                      <StatusBadge status={schedule.last_status} />
                    ) : (
                      <Text size="sm" c="dimmed">Never run</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <ActionIcon
                        variant="subtle"
                        color="blue"
                        onClick={() => handleOpenEdit(schedule)}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => handleOpenDelete(schedule)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <Modal
        opened={opened}
        onClose={close}
        title={editingSchedule ? 'Edit Payment Schedule' : 'Create Payment Schedule'}
        size="md"
      >
        <Stack gap="md">
          <Select
            label="Frequency"
            placeholder="Select frequency"
            data={frequencyOptions}
            value={formData.frequency}
            onChange={(value) => setFormData({ ...formData, frequency: value || 'monthly' })}
            required
          />

          <Select
            label="Payment Method"
            placeholder="Select payment method"
            data={paymentMethodOptions}
            value={formData.payment_method}
            onChange={(value) => setFormData({ ...formData, payment_method: value || '' })}
            required
          />

          <NumberInput
            label="Maximum Amount Cap"
            description="Leave at 0 for no limit"
            placeholder="0.00"
            prefix="$"
            decimalScale={2}
            min={0}
            value={formData.max_amount}
            onChange={(value) => setFormData({ ...formData, max_amount: Number(value) || 0 })}
          />

          <Switch
            label="Enable autopay"
            checked={formData.is_active}
            onChange={(event) =>
              setFormData({ ...formData, is_active: event.currentTarget.checked })
            }
          />

          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={close}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={createSchedule.isPending || updateSchedule.isPending}
              disabled={!formData.payment_method}
            >
              {editingSchedule ? 'Update' : 'Create'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={deleteModalOpened}
        onClose={closeDelete}
        title="Delete Payment Schedule"
        size="sm"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete this payment schedule? This action cannot be undone.
          </Text>
          {scheduleToDelete && (
            <Paper p="sm" withBorder bg="gray.0">
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" fw={500}>Frequency:</Text>
                  <Text size="sm">{getFrequencyLabel(scheduleToDelete.frequency)}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>Payment Method:</Text>
                  <Text size="sm">{getPaymentMethodLabel(scheduleToDelete.payment_method)}</Text>
                </Group>
              </Stack>
            </Paper>
          )}
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={closeDelete}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={deleteSchedule.isPending}>
              Delete Schedule
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
